import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCookie, removeCookie } from '../lib/cookieUtils';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuthStatus();
    }, []);

    const checkAuthStatus = () => {
        try {
            const accessToken = getCookie('brand_access_token');
            const refreshToken = getCookie('brand_refresh_token');

            if (!accessToken || !refreshToken) {
                setIsAuthenticated(false);
                setUser(null);
                setLoading(false);
                return;
            }

            // Basic JWT validation - check if token exists and has 3 parts
            const parts = accessToken.split('.');
            if (parts.length !== 3) {
                setIsAuthenticated(false);
                setUser(null);
                setLoading(false);
                return;
            }

            // Decode payload to get user info
            const payload = JSON.parse(atob(parts[1]));
            const now = Math.floor(Date.now() / 1000);

            if (payload.exp && payload.exp < now) {
                // Token expired, but we have refresh token - let axios interceptor handle it
                setIsAuthenticated(true);
                setUser(payload);
            } else {
                setIsAuthenticated(true);
                setUser(payload);
            }
        } catch (error) {
            console.error('Auth check error:', error);
            setIsAuthenticated(false);
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        removeCookie('brand_access_token');
        removeCookie('brand_refresh_token');
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/login';
    };

    const value = {
        user,
        isAuthenticated,
        loading,
        logout,
        checkAuthStatus
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
